import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PackagePlus, ListChecks, LayoutDashboard, TrendingUp, X, Sparkles, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'fm_welcome_seen_v1';

/** Content shown per role — first entry is the primary CTA. */
const ROLE_CONTENT = {
  EBO: {
    tagline: "You're ready to raise your first tester requisition",
    highlights: [
      { icon: PackagePlus, title: 'Raise a requisition', desc: 'Search any SKU by code or name, pick qty, submit — warehouse gets an instant email with your list.', route: '/app/raise' },
      { icon: ListChecks, title: 'My requisitions', desc: 'Track every request from Requested → Fulfilled → Shipped → Delivered with courier + tracking IDs.', route: '/app/my-requisitions' },
    ],
  },
  Warehouse: {
    tagline: "Fulfil pending requisitions from a single view",
    highlights: [
      { icon: ListChecks, title: 'All requisitions', desc: 'Filter open requests by branch, zone or state — mark items Fulfilled once they leave the shelf.', route: '/app/requisitions' },
    ],
  },
  Logistics: {
    tagline: "Ship fulfilled items and mark them delivered",
    highlights: [
      { icon: ListChecks, title: 'All requisitions', desc: 'Pick up fulfilled items, add courier + tracking IDs, mark shipped then delivered when the EBO signs.', route: '/app/requisitions' },
    ],
  },
  ZoneManager: {
    tagline: "Watch your zone's requisitions in real-time",
    highlights: [
      { icon: LayoutDashboard, title: 'Overview', desc: 'Live status of every requisition raised by branches in your zone.', route: '/app/overview' },
      { icon: TrendingUp, title: 'Reports', desc: 'Zone-level sales × tester ratios and monthly branch performance.', route: '/app/reports' },
    ],
  },
};
const DEFAULT_CONTENT = {
  tagline: "A quick tour of the two screens you'll use every day",
  highlights: [
    { icon: LayoutDashboard, title: 'Overview', desc: 'Live pulse of every branch — requisitions raised, fulfilled and shipped today.', route: '/app/overview' },
    { icon: TrendingUp, title: 'Reports', desc: 'Zone-wise sales, tester ratios, monthly rollups — export to Excel any time.', route: '/app/reports' },
  ],
};

export default function WelcomeTour() {
  const { user, theme } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!seen.includes(user.email)) setOpen(true);
  }, [user]);

  if (!open || !user) return null;

  const content = ROLE_CONTENT[user.role] || DEFAULT_CONTENT;
  const primary = content.highlights[0];

  const dismiss = () => {
    const seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!seen.includes(user.email)) seen.push(user.email);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    setOpen(false);
  };

  const goPrimary = () => { dismiss(); navigate(primary.route); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(35, 24, 28, 0.55)', backdropFilter: 'blur(6px)' }}
      onClick={dismiss}
      data-testid="welcome-tour"
    >
      <div
        className="fm-card w-full max-w-[520px] p-6 relative"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          data-testid="welcome-tour-close"
          aria-label="Close welcome tour"
          className="absolute right-4 top-4 text-[#7a6f5c] hover:text-[#232323]"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-full"
            style={{ background: theme.primary, color: '#fff' }}
          >
            <Sparkles size={15} />
          </span>
          <div className="text-[13px] text-[#7a6f5c] uppercase tracking-wide">Welcome to CORE FUTURE</div>
        </div>

        <div className="text-[20px] leading-snug font-semibold mb-1" data-testid="welcome-tour-hi">
          Hi {user.name?.split(' ')[0] || 'there'} —
        </div>
        <div className="text-[14.5px] text-[#3d3730] mb-5">{content.tagline}.</div>

        <div className="space-y-3 mb-6">
          {content.highlights.map(h => {
            const Icon = h.icon;
            return (
              <div
                key={h.route}
                className="flex items-start gap-3 p-3 border border-[#EFEADC] rounded-lg bg-[#FBFAF7]"
                data-testid={`welcome-tour-highlight-${h.route.split('/').pop()}`}
              >
                <span
                  className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0"
                  style={{ background: theme.accent || '#F5F1E7', color: theme.primary }}
                >
                  <Icon size={14} />
                </span>
                <div>
                  <div className="text-[13.5px] font-semibold text-[#232323]">{h.title}</div>
                  <div className="text-[12.5px] text-[#7a6f5c] leading-relaxed mt-0.5">{h.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={dismiss}
            data-testid="welcome-tour-explore-btn"
            className="text-[13px] text-[#7a6f5c] hover:text-[#232323] underline underline-offset-4"
          >
            I'll explore on my own
          </button>
          <button
            onClick={goPrimary}
            data-testid="welcome-tour-primary-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-[13.5px] font-medium"
            style={{ background: theme.primary }}
          >
            Take me to {primary.title}
            <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
